import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVault } from '@/lib/supabase/VaultContext';
import { useAuth } from '@/lib/supabase/AuthContext';
import { TagSelector } from './TagSelector';
import { PasswordGenerator } from './PasswordGenerator';
import { TipTapEditor } from './TipTapEditor';
import { Button } from '@/components/ui/Button';
import { getPayloadSchema } from '@/lib/supabase/schemas';
import type { ItemType } from '@/lib/supabase/types';
import { spring } from '@/lib/motion/presets';
import { getUploadUrl, stripExifAndGetDimensions, uploadFileWithProgress } from '@/lib/r2/storage';
import {
  X,
  ChevronLeft,
  KeyRound,
  FileText,
  CreditCard,
  UserSquare,
  Binary,
  Star,
  Loader2,
  Sparkles,
  FileImage,
  FileUp,
  UploadCloud,
  FileCheck,
  AlertCircle,
  Trash2,
  RefreshCw,
} from 'lucide-react';

interface ItemCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ADDABLE_TYPES: { type: ItemType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    type: 'password',
    label: 'Password',
    description: 'Store login credentials, usernames, and secret URLs.',
    icon: <KeyRound className="size-5 text-accent" />,
  },
  {
    type: 'note',
    label: 'Secure Note',
    description: 'Write formatted documents, checklists, or personal journals.',
    icon: <FileText className="size-5 text-accent" />,
  },
  {
    type: 'card',
    label: 'Credit Card',
    description: 'Save cardholders, card numbers, expiry dates, and CVVs.',
    icon: <CreditCard className="size-5 text-accent" />,
  },
  {
    type: 'id',
    label: 'ID / Document',
    description: 'Keep driver\'s licenses, passports, and membership numbers.',
    icon: <UserSquare className="size-5 text-accent" />,
  },
  {
    type: 'software_key',
    label: 'Software Key',
    description: 'Record license keys, activation codes, and purchase emails.',
    icon: <Binary className="size-5 text-accent" />,
  },
  {
    type: 'file',
    label: 'Secure File',
    description: 'Upload PDF, document, archive, or any key file securely.',
    icon: <FileUp className="size-5 text-accent" />,
  },
  {
    type: 'photo',
    label: 'Secure Photo',
    description: 'Store high-resolution photos with EXIF metadata stripped.',
    icon: <FileImage className="size-5 text-accent" />,
  },
];

export function ItemCreateModal({ isOpen, onClose }: ItemCreateModalProps) {
  const { createItem } = useVault();
  const { session } = useAuth();
  
  const [step, setStep] = useState<'select' | 'form'>('select');
  const [selectedType, setSelectedType] = useState<ItemType | null>(null);
  
  // Form fields
  const [title, setTitle] = useState('');
  const [favorite, setFavorite] = useState(false);
  const [payload, setPayload] = useState<Record<string, any>>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // Upload States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'stripping' | 'presigning' | 'uploading' | 'completed' | 'failed'>('idle');
  const [uploadXhr, setUploadXhr] = useState<XMLHttpRequest | null>(null);
  const [storageRef, setStorageRef] = useState<string | null>(null);
  const [storageMeta, setStorageMeta] = useState<any>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGenerator, setShowGenerator] = useState(false);

  function abortUpload() {
    if (uploadXhr) {
      uploadXhr.abort();
    }
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setUploadXhr(null);
    setStorageRef(null);
    setStorageMeta(null);
  }

  function resetForm() {
    abortUpload();
    setStep('select');
    setSelectedType(null);
    setTitle('');
    setFavorite(false);
    setPayload({});
    setSelectedTags([]);
    setError(null);
    setLoading(false);
    setShowGenerator(false);
  }

  function handleTypeSelect(type: ItemType) {
    setSelectedType(type);
    
    // Initialize default payload fields based on type
    const schema = getPayloadSchema(type);
    const defaults = schema.parse({});
    setPayload(defaults);
    
    setStep('form');
  }

  function updatePayloadField(field: string, value: any) {
    setPayload((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const startUpload = async (fileToUpload: File) => {
    if (!session?.access_token) {
      setError('Not authenticated');
      setUploadStatus('failed');
      return;
    }

    setSelectedFile(fileToUpload);
    setUploadProgress(0);
    setError(null);

    // Auto-fill title with filename (without extension) if title is empty
    if (!title) {
      const nameWithoutExt = fileToUpload.name.replace(/\.[^/.]+$/, "");
      setTitle(nameWithoutExt);
    }

    try {
      let blob: Blob = fileToUpload;
      let imgWidth: number | undefined;
      let imgHeight: number | undefined;

      // 1. Strip EXIF if photo
      if (selectedType === 'photo') {
        setUploadStatus('stripping');
        const stripped = await stripExifAndGetDimensions(fileToUpload);
        blob = stripped.blob;
        imgWidth = stripped.width;
        imgHeight = stripped.height;
      }

      // 2. Get presigned URL
      setUploadStatus('presigning');
      const { url, key, error: presignError } = await getUploadUrl(
        session.access_token,
        fileToUpload.name,
        fileToUpload.type || 'application/octet-stream',
        blob.size
      );

      if (presignError) {
        throw new Error(presignError);
      }

      // 3. Perform upload with progress
      setUploadStatus('uploading');
      const { xhr, promise } = uploadFileWithProgress(
        blob,
        url,
        fileToUpload.type || 'application/octet-stream',
        (progress) => {
          setUploadProgress(progress);
        }
      );

      setUploadXhr(xhr);
      await promise;

      // 4. Complete upload
      setUploadStatus('completed');
      setStorageRef(key);
      setStorageMeta({
        size: blob.size,
        mime: fileToUpload.type || 'application/octet-stream',
        original_name: fileToUpload.name,
        width: imgWidth,
        height: imgHeight,
      });
      setUploadXhr(null);
    } catch (err: any) {
      console.error('File upload failed:', err);
      if (err.message !== 'Upload aborted by user.') {
        setError(err.message || 'Upload failed.');
        setUploadStatus('failed');
      }
      setUploadXhr(null);
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedType) return;
    
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if ((selectedType === 'file' || selectedType === 'photo') && uploadStatus !== 'completed') {
      setError('Please wait for the file to finish uploading.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate payload with Zod schema
      const schema = getPayloadSchema(selectedType);
      const validatedPayload = schema.parse(payload);

      const { error: dbError } = await createItem(
        {
          title: title.trim(),
          item_type: selectedType,
          payload: validatedPayload,
          favorite,
          storage_ref: storageRef,
          storage_meta: storageMeta,
        },
        selectedTags
      );

      if (dbError) throw new Error(dbError);

      resetForm();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create item.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="fixed inset-0 bg-bg-deep/80 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={spring}
            className="relative z-10 w-full max-w-lg overflow-hidden rounded-lg border border-line bg-surface shadow-accent-glow"
          >
            {/* Header */}
            <div className="flex h-14 items-center justify-between border-b border-line px-5">
              <div className="flex items-center gap-2">
                {step === 'form' && (
                  <button
                    type="button"
                    onClick={() => {
                      setStep('select');
                      setError(null);
                      setShowGenerator(false);
                    }}
                    className="rounded p-1 text-ink-muted hover:bg-bg hover:text-ink transition-colors"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                )}
                <h2 className="text-sm font-semibold tracking-tight">
                  {step === 'select' ? 'Add to Vault' : `New ${selectedType ? selectedType.replace('_', ' ') : ''}`}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                className="rounded p-1 text-ink-muted hover:bg-bg hover:text-ink transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="max-h-[75vh] overflow-y-auto p-5 space-y-5">
              {error && (
                <div className="rounded border border-danger/20 bg-danger/5 px-3 py-2 text-xs text-danger">
                  {error}
                </div>
              )}

              {step === 'select' ? (
                /* Step 1: Type Selection */
                <div className="grid gap-3">
                  {ADDABLE_TYPES.map((t) => (
                    <button
                      key={t.type}
                      type="button"
                      onClick={() => handleTypeSelect(t.type)}
                      className="group flex items-start gap-4 rounded-md border border-line bg-bg/30 p-3.5 text-left hover:border-line-strong hover:bg-bg/70 transition-all"
                    >
                      <span className="grid size-9 place-items-center rounded bg-surface border border-line group-hover:shadow-accent-glow transition-shadow">
                        {t.icon}
                      </span>
                      <div className="flex-1 space-y-0.5">
                        <h4 className="text-xs font-semibold text-ink group-hover:text-accent transition-colors">
                          {t.label}
                        </h4>
                        <p className="text-2xs text-ink-muted leading-normal">
                          {t.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                /* Step 2: Dynamic Form */
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Common Fields: Title & Favorite */}
                  <div className="flex items-end gap-3">
                    <div className="flex-1 space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Title</label>
                      <input
                        type="text"
                        required
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`e.g., My ${selectedType ? selectedType.replace('_', ' ') : 'item'}`}
                        className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setFavorite((f) => !f)}
                      className={`grid size-9 place-items-center rounded-md border transition-colors ${
                        favorite
                          ? 'border-accent bg-accent/5 text-accent'
                          : 'border-line bg-surface text-ink-faint hover:text-ink-muted'
                      }`}
                      title="Favorite"
                    >
                      <Star className="size-4 fill-current" />
                    </button>
                  </div>

                  {/* Type Specific Fields */}
                  {(selectedType === 'file' || selectedType === 'photo') && (
                    <div className="space-y-4">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">
                        {selectedType === 'photo' ? 'Photo Upload' : 'File Upload'}
                      </label>

                      {!selectedFile ? (
                        /* Drag and Drop Zone */
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragActive(true);
                          }}
                          onDragLeave={() => setIsDragActive(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDragActive(false);
                            const files = e.dataTransfer.files;
                            if (files && files.length > 0) {
                              startUpload(files[0]);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            if (selectedType === 'photo') {
                              input.accept = 'image/jpeg,image/png,image/webp';
                            }
                            input.onchange = (e) => {
                              const files = (e.target as HTMLInputElement).files;
                              if (files && files.length > 0) {
                                startUpload(files[0]);
                              }
                            };
                            input.click();
                          }}
                          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer transition-all ${
                            isDragActive
                              ? 'border-accent bg-accent/5 shadow-accent-glow'
                              : 'border-line bg-bg/25 hover:border-line-strong hover:bg-bg/40'
                          }`}
                        >
                          <UploadCloud className="size-8 text-ink-faint mb-2.5 transition-colors" />
                          <p className="text-xs font-semibold text-ink text-center">
                            Drag & drop your {selectedType} here, or <span className="text-accent">browse</span>
                          </p>
                          <p className="text-4xs text-ink-faint uppercase tracking-wider mt-1.5 font-mono text-center">
                            {selectedType === 'photo'
                              ? 'JPEG, PNG, WEBP ONLY — EXIF STRIPPED'
                              : 'ANY FILE TYPE'}
                          </p>
                        </div>
                      ) : (
                        /* Upload Progress Card */
                        <div className="rounded-md border border-line bg-bg/30 p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <span className="grid size-9 place-items-center rounded bg-surface border border-line">
                              {selectedType === 'photo' ? (
                                <FileImage className="size-4.5 text-accent" />
                              ) : (
                                <FileUp className="size-4.5 text-accent" />
                              )}
                            </span>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-xs font-semibold text-ink truncate">
                                {selectedFile.name}
                              </h5>
                              <p className="text-3xs text-ink-muted font-mono mt-0.5">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                            
                            {/* Action Button */}
                            {uploadStatus === 'failed' ? (
                              <button
                                type="button"
                                onClick={() => startUpload(selectedFile)}
                                className="rounded p-1 text-accent hover:bg-accent/10 transition-colors"
                                title="Retry Upload"
                              >
                                <RefreshCw className="size-4" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={abortUpload}
                                className="rounded p-1 text-ink-faint hover:bg-danger/10 hover:text-danger transition-colors"
                                title={uploadStatus === 'completed' ? 'Remove File' : 'Cancel Upload'}
                              >
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </div>

                          {/* Progress bar / Status */}
                          <div className="space-y-1.5">
                            <div className="flex justify-between items-center text-3xs font-medium">
                              <span className={`flex items-center gap-1 ${
                                uploadStatus === 'completed' ? 'text-success' :
                                uploadStatus === 'failed' ? 'text-danger' :
                                'text-ink-muted'
                              }`}>
                                {uploadStatus === 'stripping' && 'Stripping EXIF metadata...'}
                                {uploadStatus === 'presigning' && 'Creating secure slot...'}
                                {uploadStatus === 'uploading' && `Uploading...`}
                                {uploadStatus === 'completed' && (
                                  <>
                                    <FileCheck className="size-3.5" />
                                    <span>Ready to save</span>
                                  </>
                                )}
                                {uploadStatus === 'failed' && (
                                  <>
                                    <AlertCircle className="size-3.5" />
                                    <span>Upload failed</span>
                                  </>
                                )}
                              </span>
                              <span className="font-mono text-ink-muted">
                                {uploadStatus === 'completed' ? '100%' : `${uploadProgress}%`}
                              </span>
                            </div>

                            {uploadStatus !== 'completed' && uploadStatus !== 'failed' && (
                              <div className="h-1.5 w-full rounded-full bg-bg overflow-hidden border border-line">
                                <motion.div
                                  className="h-full bg-accent"
                                  initial={{ width: 0 }}
                                  animate={{
                                    width: uploadStatus === 'uploading' ? `${uploadProgress}%` : '5%'
                                  }}
                                  transition={{ duration: 0.1 }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedType === 'password' && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Username / Email</label>
                        <input
                          type="text"
                          value={payload.username || ''}
                          onChange={(e) => updatePayloadField('username', e.target.value)}
                          placeholder="username@email.com"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Password</label>
                          <button
                            type="button"
                            onClick={() => setShowGenerator((g) => !g)}
                            className="flex items-center gap-1 text-2xs text-accent hover:text-accent-soft transition-colors"
                          >
                            <Sparkles className="size-3" />
                            <span>{showGenerator ? 'Hide Generator' : 'Generate'}</span>
                          </button>
                        </div>
                        <input
                          type="text" // Plain text for visibility during creation
                          value={payload.password || ''}
                          onChange={(e) => updatePayloadField('password', e.target.value)}
                          placeholder="••••••••••••"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>

                      {/* Password Generator Dropdown */}
                      <AnimatePresence>
                        {showGenerator && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <PasswordGenerator
                              onUsePassword={(p) => {
                                updatePayloadField('password', p);
                                setShowGenerator(false);
                              }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Website URL</label>
                        <input
                          type="text"
                          value={payload.url || ''}
                          onChange={(e) => updatePayloadField('url', e.target.value)}
                          placeholder="https://example.com"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                  )}

                  {selectedType === 'note' && (
                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Note Content</label>
                      <TipTapEditor
                        content={payload.content || ''}
                        onChange={(html) => updatePayloadField('content', html)}
                      />
                    </div>
                  )}

                  {selectedType === 'card' && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Cardholder Name</label>
                        <input
                          type="text"
                          value={payload.cardholderName || ''}
                          onChange={(e) => updatePayloadField('cardholderName', e.target.value)}
                          placeholder="Shadman Ahnaf"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Card Number</label>
                        <input
                          type="text"
                          maxLength={19}
                          value={payload.cardNumber || ''}
                          onChange={(e) => {
                            // Basic card space formatting
                            const val = e.target.value.replace(/\s?/g, '').replace(/(\d{4})/g, '$1 ').trim();
                            updatePayloadField('cardNumber', val);
                          }}
                          placeholder="4000 1234 5678 9010"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Expiry Month</label>
                          <input
                            type="text"
                            maxLength={2}
                            value={payload.expiryMonth || ''}
                            onChange={(e) => updatePayloadField('expiryMonth', e.target.value)}
                            placeholder="MM"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-center text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Expiry Year</label>
                          <input
                            type="text"
                            maxLength={4}
                            value={payload.expiryYear || ''}
                            onChange={(e) => updatePayloadField('expiryYear', e.target.value)}
                            placeholder="YYYY"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-center text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">CVV</label>
                          <input
                            type="text"
                            maxLength={4}
                            value={payload.cvv || ''}
                            onChange={(e) => updatePayloadField('cvv', e.target.value)}
                            placeholder="123"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-center text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedType === 'id' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Document Type</label>
                          <input
                            type="text"
                            value={payload.type || ''}
                            onChange={(e) => updatePayloadField('type', e.target.value)}
                            placeholder="e.g., Passport, Driver's License"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Country</label>
                          <input
                            type="text"
                            value={payload.country || ''}
                            onChange={(e) => updatePayloadField('country', e.target.value)}
                            placeholder="e.g., Bangladesh, USA"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">ID Number</label>
                          <input
                            type="text"
                            value={payload.number || ''}
                            onChange={(e) => updatePayloadField('number', e.target.value)}
                            placeholder="e.g., NID-12345"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Expiry Date</label>
                          <input
                            type="date"
                            value={payload.expiryDate || ''}
                            onChange={(e) => updatePayloadField('expiryDate', e.target.value)}
                            className="w-full rounded-md border border-line bg-surface px-3 py-1.5 text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedType === 'software_key' && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">License Key</label>
                        <input
                          type="text"
                          value={payload.licenseKey || ''}
                          onChange={(e) => updatePayloadField('licenseKey', e.target.value)}
                          placeholder="AAAA-BBBB-CCCC-DDDD"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs font-mono text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Registered Email</label>
                          <input
                            type="email"
                            value={payload.email || ''}
                            onChange={(e) => updatePayloadField('email', e.target.value)}
                            placeholder="purchaser@email.com"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Version / Edition</label>
                          <input
                            type="text"
                            value={payload.version || ''}
                            onChange={(e) => updatePayloadField('version', e.target.value)}
                            placeholder="e.g., Pro, v2.0"
                            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Purchase URL</label>
                        <input
                          type="text"
                          value={payload.url || ''}
                          onChange={(e) => updatePayloadField('url', e.target.value)}
                          placeholder="https://store.example.com"
                          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                  )}

                  {/* Shared Notes Field (except for Note type which is already rich text) */}
                  {selectedType !== 'note' && (
                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Notes</label>
                      <textarea
                        rows={2}
                        value={payload.notes || ''}
                        onChange={(e) => updatePayloadField('notes', e.target.value)}
                        placeholder="Additional details..."
                        className="w-full rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-line-strong focus:outline-none transition-colors resize-none"
                      />
                    </div>
                  )}

                  {/* Tag Selector */}
                  <TagSelector selectedTags={selectedTags} onChange={setSelectedTags} />

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-3 border-t border-line">
                    <Button
                      type="button"
                      variant="subtle"
                      size="md"
                      onClick={() => {
                        resetForm();
                        onClose();
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      disabled={loading || ((selectedType === 'file' || selectedType === 'photo') && uploadStatus !== 'completed')}
                      className="flex items-center gap-1.5"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="size-3.5 animate-spin" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <span>Save to Vault</span>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
